/*
 * (c) Copyright, 2025, Medidata Solutions Worldwide, Inc. All rights reserved.
 */

import React, { useEffect, useState, useRef } from 'react'
import { Formik } from 'formik'
import cloneDeep from 'lodash/cloneDeep'
import _ from 'underscore'
import moment from 'moment'
import DatePicker from 'react-datepicker'
import { useTranslation } from 'react-i18next'
import Modal from '~/components/Modal'
import { Select } from '~/components/Select/Select'
import { transformToSelectOptions } from '~/utils/selectOptions'
import { convertDateForServer } from '~/utils/dateFormatting'
import { convertTimestampToUTCMoment } from '~/utils/dateUtils'
import { GroupType } from '~/constants/group'
import FsiPropertiesInlineEditor from '~/components/doc-preview/properties/FsiPropertiesInlineEditor'
import { useServiceContext } from '~/root'
import DatePickerWrapper from '~/components/DatePicker/DatePickerWrapper'

type MetadataType =
  | 'DATETIME'
  | 'STRING'
  | 'NUMBER'
  | 'SELECT_ONE'
  | 'SELECT_MULTI'
  | 'ARTIFACT'
  | 'SELECT_ONE_USER'
  | 'SELECT_MULTI_USER'
  | 'SELECT_ONE_GROUP'
  | 'SELECT_MULTI_GROUP'

interface ArtifactPropertiesProps {
  documentTypeId: number | string
  setPckgData: (updater: (prev: any) => any) => void
  artifact: { id: number; name: string }
  options?: { selectedIds?: number[] }
  docTypeIds: number[]
  level: string
}

const ArtifactProperties: React.FC<ArtifactPropertiesProps> = ({
  documentTypeId,
  setPckgData,
  artifact,
  options,
  docTypeIds,
  level
}) => {
  const context = useServiceContext()
  const { t: translate } = useTranslation()
  const { filter } = context.service
  const MetaType = {
    DATETIME: 'DATETIME',
    STRING: 'STRING',
    NUMBER: 'NUMBER',
    SELECT_ONE: 'SELECT_ONE',
    SELECT_MULTI: 'SELECT_MULTI',
    ARTIFACT: 'ARTIFACT',
    SELECT_ONE_USER: 'SELECT_ONE_USER',
    SELECT_MULTI_USER: 'SELECT_MULTI_USER',
    SELECT_ONE_GROUP: 'SELECT_ONE_GROUP',
    SELECT_MULTI_GROUP: 'SELECT_MULTI_GROUP'
  }
  const { toast } = context.service
  const { date } = filter

  const [defaultFilter, setDefaultFilter] = useState<any>({})
  const [userOptions, setUserOptions] = useState<any>(null)
  const [groupOptions, setGroupOptions] = useState<any>(null)
  const [groupOptionsCaRolesDisabled, setGroupOptionsCaRolesDisabled] = useState<any>(null)
  const [isOpen, setIsOpen] = useState(true)
  const [documentType, setDocumentType] = useState<any>({})
  const [metadata, setMetadata] = useState<any>(null)
  const [hasChanges, setHasChanges] = useState(false)

  const users = useRef<any>(null)
  const groups = useRef<any>(null)

  function updateMetadata(prop: any, propClone?: any) {
    const assembleMetadata = _.clone(metadata)
    if (propClone) {
      const index = _.indexOf(metadata, prop)
      assembleMetadata.splice(index, 1, propClone)
    }
    setNewFilter(assembleMetadata)
    setMetadata(assembleMetadata)
  }

  function onCancelItem(property: any) {
    const index = _.indexOf(metadata, property)
    const assembleMetadata = _.clone(metadata)
    assembleMetadata.splice(index, 1, _.clone(metadata[index]))
    setNewFilter(assembleMetadata)
    setMetadata(assembleMetadata)
  }

  function revertToInherited(item: any) {
    _.extend(item, {
      value: _.clone(item.itemCopy.value),
      revertToInherited: true,
      inherited: true,
      isChanged: false
    })
    onCancelItem(item)

    return Promise.resolve()
  }

  function onRevertItem(property: any) {
    return revertToInherited(property)
  }

  function onChangeItem(prop: any, value: any, isReturnToInherited?: boolean) {
    if (
      prop.type === 'DATETIME' &&
      prop.value &&
      _.isFunction(prop.value.getDate) &&
      prop.itemCopy &&
      prop.itemCopy.value &&
      _.isFunction(prop.itemCopy.value.getDate) &&
      prop.value.getDate() === prop.itemCopy.value.getDate()
    ) {
      // dateNotEdited = true; ??
    }

    const propClone = _.clone(prop)
    _.extend(propClone, {
      revertToInherited: !!isReturnToInherited,
      value: value
    })

    if (propClone.itemCopy && _.isString(propClone.itemCopy.value) && propClone.itemCopy.value === propClone.value) {
      _.extend(propClone, {
        isChanged: false,
        inherited: propClone.itemCopy.inherited
      })
    } else {
      _.extend(propClone, {
        isChanged: true,
        inherited: !!isReturnToInherited
      })
    }
    updateMetadata(prop, propClone)
  }

  const onAcceptItem = (prop: any) => {
    if (
      prop.type === 'DATETIME' &&
      prop.value &&
      _.isFunction(prop.value.getDate) &&
      prop.itemCopy &&
      prop.itemCopy.value &&
      _.isFunction(prop.itemCopy.value.getDate) &&
      prop.value.getDate() === prop.itemCopy.value.getDate()
    ) {
      // dateNotEdited = true; ??
    }

    if (prop.itemCopy && _.isString(prop.itemCopy.value) && prop.itemCopy.value === prop.value) {
      _.extend(prop, {
        isChanged: false,
        revertToInherited: false,
        inherited: prop.itemCopy.inherited
      })
    } else {
      _.extend(prop, {
        isChanged: true,
        revertToInherited: false,
        inherited: false
      })
    }

    updateMetadata(prop)
  }

  useEffect(() => {
    let isChanged = false

    if (Array.isArray(metadata)) {
      metadata.forEach((property: any) => {
        if (property.isChanged) {
          isChanged = true
        }
      })
    }
    setHasChanges(!!isChanged)
  }, [metadata])

  const isMultiSelectionOfArtifact = () => {
    return options?.selectedIds?.length && options.selectedIds.length > 1
  }

  useEffect(() => {
    docTypeChangedNew(
      isMultiSelectionOfArtifact() ? options?.selectedIds : artifact?.id,
      docTypeIds?.length > 1 ? Array.from(new Set(docTypeIds)) : documentTypeId,
      level
    )
    loadUserGroups()
  }, [])

  const loadUserGroups = () => {
    const promises: any[] = []

    promises.push(context.api.users.getAssembledUsersGroups())
    promises[0].then((resp: any) => {
      users.current = _.clone(resp.usersAll)
      groups.current = _.clone(resp.groups)

      setUserOptions(transformToSelectOptions(users.current, 'id', 'name', 'name', true))
      setGroupOptions(transformToSelectOptions(groups.current, 'id', 'name', 'name'))
      setGroupOptionsCaRolesDisabled(
        transformToSelectOptions(groups.current, 'id', 'name', 'name', true).map((option: any) => {
          return {
            label: option.label,
            value: option.value,
            ...(option?.type === GroupType.CLOUDADMIN_ACL ? { isDisabled: true } : {})
          }
        })
      )
    })
  }

  const save = (values: any) => {
    const assembledMetadataForPayload = _(cloneDeep(metadata)).map((mtdta: any) => {
      return {
        metadataTemplateId: mtdta?.id,
        value: mtdta.type === 'DATETIME' ? convertDateForServer(values[mtdta.name]) : values[mtdta.name]
      }
    })

    if (isMultiSelectionOfArtifact()) {
      setPckgData((prev: any) =>
        _.extend(prev, {
          ...Object.fromEntries(
            (options?.selectedIds || []).map((key) => [`ARTIFACT_ID_${key}`, assembledMetadataForPayload])
          )
        })
      )
    } else {
      setPckgData((prev: any) => _.extend(prev, { [`ARTIFACT_ID_${artifact?.id}`]: assembledMetadataForPayload }))
    }

    setIsOpen(false)
  }

  const docTypeChangedNew = async (artifact: any, docTypeIdes: any, level: string) => {
    let filePropIds: any
    let mets: any
    await context.api.packages
      .getDetailsForPropertiesMultipleArtifactsPost(artifact, docTypeIdes, level)
      .then((data: any) => {
        mets = data
        filePropIds = data.metadata.elements?.map(function (a: any) {
          return a.metadataTemplateId
        })
      })

    if (docTypeIds?.length < 2) {
      await context.api.documentTypes.getById(Number(documentTypeId)).then((data: any) => {
        setDocumentType(data)
      })
    }

    if (mets?.metadata?.totalElements > 0) {
      await context.api.metadata.getAll({ id: filePropIds ? filePropIds : 0 }).then(function (resp: any) {
        const metadataList = resp.elements
        _(metadataList).forEach(function (prop: any) {
          prop.value = prop.definition.defaultValue
          prop.itemCopy = prop
        })
        metadataList.forEach((elm: any) => {
          return _(mets.metadata.elements).forEach((ml: any) => {
            if (elm.id === ml.metadataTemplateId) {
              return _.extend(elm, { value: ml.value, fromArtifact: ml })
            }
          })
        })

        setNewFilter(metadataList)
        setMetadata(metadataList)
      })
    } else {
      toast.info(translate('NO_COMMON_PROP'))
    }
  }

  function setNewFilter(metadataList: any) {
    const initialVal = metadataList?.reduce((acc: any, prop: any) => {
      acc[prop.name] = [
        MetaType.SELECT_ONE,
        MetaType.STRING,
        MetaType.NUMBER,
        MetaType.SELECT_ONE_GROUP,
        MetaType.SELECT_ONE_USER,
        MetaType.ARTIFACT
      ].includes(prop?.type)
        ? prop?.value
        : [MetaType.SELECT_MULTI, MetaType.SELECT_MULTI_USER, MetaType.SELECT_MULTI_GROUP, MetaType.ARTIFACT].includes(
              prop?.type
            )
          ? prop?.value || []
          : [MetaType.DATETIME].includes(prop?.type)
            ? prop?.value && moment(prop?.value)
            : null
      return acc
    }, {})
    setDefaultFilter(initialVal)
  }

  const doClose = () => {
    setIsOpen(() => false)
  }

  return (
    <Modal show={isOpen} handleClose={doClose} title={translate('BROWSER_PROPERTIES_H_TITLE')} size="lg">
      <Formik
        initialValues={defaultFilter}
        innerRef={(el: any) => {
          ;(context.store as any).formRefProperties = el
        }}
        enableReinitialize={true}
        onSubmit={() => {}}
      >
        {({ values, setFieldValue, errors, setFieldTouched, touched }: any) => (
          <>
            <Modal.Body>
              <div id="properties-dialog">
                <div>
                  <div className="properties-label">
                    <label className="mb0">{translate('ETMF_ARTIFACT')}</label>
                    <p className="p-value">
                      {isMultiSelectionOfArtifact() ? translate('MULTIPLE_ARTIFACTS') : artifact?.name}
                    </p>
                  </div>
                  <div className="properties-label">
                    <label className="mb0">{translate('DOCUMENT_TYPE')}</label>
                    <p className="p-value">
                      {docTypeIds?.length > 1 ? translate('WORKFLOW_MULTIPLE') : documentType?.name}
                    </p>
                  </div>
                </div>
                <div>
                  <div>
                    <label className="mb0">
                      <span>{translate('PROPERTIES')}</span>
                    </label>
                  </div>
                </div>
                <div>
                  <table className="metadatatable" style={{ border: 'none' }}>
                    <thead>
                      <tr>
                        <th className="name">{translate('NAME')}</th>
                        <th className="value">
                          <span>{translate('VALUE')}</span>
                        </th>
                      </tr>
                    </thead>
                    <tbody>
                      {metadata?.map((property: any, propIndex: number) => {
                        return (
                          <tr key={propIndex} className="new">
                            <td className={`property-name`}>
                              <span>{property.name}</span>
                            </td>
                            <td className="form-group md-form-group w100p ">
                              {property &&
                                {
                                  DATETIME: (
                                    <FsiPropertiesInlineEditor
                                      property={property}
                                      onAccept={onAcceptItem}
                                      onRevert={onRevertItem}
                                      onCancel={onCancelItem}
                                      isEditable={true}
                                      isRequired={false}
                                      value={() => {
                                        return property.value
                                          ? date(
                                              moment.isMoment(property.value)
                                                ? property.value.toDate()
                                                : convertTimestampToUTCMoment(property.value).toDate(),
                                              true
                                            )
                                          : null
                                      }}
                                      input={
                                        <DatePickerWrapper
                                          alwaysEditable={true}
                                          value={() => {
                                            return property.value
                                              ? date(
                                                  moment.isMoment(property.value)
                                                    ? property.value.toDate()
                                                    : convertTimestampToUTCMoment(property.value).toDate(),
                                                  true
                                                )
                                              : null
                                          }}
                                          input={
                                            <DatePicker
                                              name={property.name}
                                              selected={
                                                property.value
                                                  ? moment.isMoment(property.value)
                                                    ? property.value.toDate()
                                                    : moment(property.value).toDate()
                                                  : null
                                              }
                                              placeholderText={translate('DATE_FORMAT_YYYYMMDD')}
                                              onChange={(val: any) => {
                                                onChangeItem(property, val)
                                              }}
                                              dateFormat="yyyyMMdd hh:mm:ss a"
                                              showYearDropdown
                                              className="form-control field-control"
                                              onBlur={() => {
                                                setFieldTouched(property.name, true)
                                              }}
                                              timeInputLabel={translate('TIME_LABEL_CLINICAL_ACTIVITY_REPORT')}
                                            />
                                          }
                                        />
                                      }
                                    />
                                  ),
                                  STRING: (
                                    <FsiPropertiesInlineEditor
                                      property={property}
                                      onAccept={onAcceptItem}
                                      onRevert={onRevertItem}
                                      onCancel={onCancelItem}
                                      isEditable
                                      isRequired={false}
                                      value={property.value}
                                      input={
                                        <input
                                          name={`${property.name}`}
                                          value={values?.[property.name]}
                                          type="text"
                                          className="editable-input form-control"
                                          id={property.name}
                                          onChange={(event) => {
                                            onChangeItem(property, event.target.value)
                                            setFieldValue(`${property.name}`, event.target.value)
                                          }}
                                          onBlur={() => {
                                            setFieldTouched(property.name, true)
                                          }}
                                        />
                                      }
                                    />
                                  ),
                                  NUMBER: (
                                    <FsiPropertiesInlineEditor
                                      property={property}
                                      onAccept={onAcceptItem}
                                      onRevert={onRevertItem}
                                      onCancel={onCancelItem}
                                      isEditable={true}
                                      isRequired={false}
                                      value={property.value}
                                      input={
                                        <input
                                          value={values?.[property.name]}
                                          name={property.name}
                                          type="number"
                                          id={property.name}
                                          className="editable-input form-control"
                                          onPaste={(e) => {
                                            e.preventDefault()
                                          }}
                                          onKeyDown={(e) => {
                                            // copied from angularjs version
                                            if (
                                              !(
                                                (e.keyCode >= 48 && e.keyCode <= 57) ||
                                                (e.keyCode >= 96 && e.keyCode <= 105) ||
                                                _.includes([8, 37, 38, 39, 40, 46], e.keyCode)
                                              )
                                            ) {
                                              e.preventDefault()
                                            }
                                          }}
                                          onChange={(e) => {
                                            onChangeItem(property, e.target.value)
                                            const parsedValue =
                                              typeof e.target?.value === 'string'
                                                ? e.target?.value === ''
                                                  ? null
                                                  : Number(e.target?.value)
                                                : e.target?.value
                                            setFieldValue(`${property.name}`, parsedValue)
                                          }}
                                          onBlur={() => {
                                            setFieldTouched(property.name, true)
                                          }}
                                        />
                                      }
                                    />
                                  ),
                                  SELECT_ONE: (
                                    <FsiPropertiesInlineEditor
                                      property={property}
                                      onAccept={onAcceptItem}
                                      onRevert={onRevertItem}
                                      onCancel={onCancelItem}
                                      isEditable={true}
                                      isRequired={false}
                                      input={
                                        <Select
                                          value={values?.[property.name]}
                                          name={`${property.name}`}
                                          placeholder={translate('ITEM(S)...')}
                                          keyup="keyPressed($event,'select-one',propIndex)"
                                          options={transformToSelectOptions(property.definition.options)}
                                          multi={false}
                                          searchable
                                          onChange={(option: any) => {
                                            onChangeItem(property, option.value)
                                            setFieldValue(`${property.name}`, option ? option.value : null)
                                          }}
                                          id={property.name}
                                          onBlur={() => {
                                            setFieldTouched(property.name, true)
                                          }}
                                        />
                                      }
                                    />
                                  ),
                                  ARTIFACT: (
                                    <FsiPropertiesInlineEditor
                                      property={property}
                                      onAccept={onAcceptItem}
                                      onRevert={onRevertItem}
                                      onCancel={onCancelItem}
                                      isEditable={false}
                                      isRequired={false}
                                      value={() => values?.[property.name]}
                                      input={
                                        <Select
                                          value={values?.[property.name]}
                                          name={`property_${propIndex}`}
                                          placeholder={translate('ITEM(S)...')}
                                          autoFocus
                                          isDisabled={true}
                                          options={[
                                            {
                                              label: property?.value,
                                              value: property?.value
                                            }
                                          ]}
                                          searchable
                                          isClearable={false}
                                        />
                                      }
                                    />
                                  ),
                                  SELECT_MULTI: (
                                    <FsiPropertiesInlineEditor
                                      property={property}
                                      onAccept={onAcceptItem}
                                      onRevert={onRevertItem}
                                      onCancel={onCancelItem}
                                      value={() => values?.[property.name]}
                                      isEditable={true}
                                      isRequired={false}
                                      input={
                                        <Select
                                          value={values?.[property.name]}
                                          name={`${property.name}`}
                                          placeholder={translate('ITEM(S)...')}
                                          keyup="keyPressed($event,'select-one',propIndex)"
                                          options={transformToSelectOptions(property?.definition?.options)}
                                          multi={true}
                                          searchable
                                          onChange={(option: any) => {
                                            const values = option?.map((o: any) => o.value) || []
                                            onChangeItem(property, values)
                                            setFieldValue(`${property.name}`, values)
                                          }}
                                          id={property.name}
                                          onBlur={() => {
                                            setFieldTouched(property.name, true)
                                          }}
                                        />
                                      }
                                    />
                                  ),
                                  SELECT_ONE_USER: (
                                    <FsiPropertiesInlineEditor
                                      property={property}
                                      onAccept={onAcceptItem}
                                      onRevert={onRevertItem}
                                      onCancel={onCancelItem}
                                      value={() => values?.[property.name]}
                                      isEditable={true}
                                      isRequired={false}
                                      input={
                                        <Select
                                          value={values?.[property.name]}
                                          name={property.name}
                                          placeholder={translate('ALL_USERS')}
                                          options={userOptions}
                                          multi={false}
                                          searchable
                                          disabled={property.value && !property.value.enabled}
                                          filter={(user: any) => {
                                            // options
                                            return user.enabled || (property.value && user.id === property.value.id)
                                          }}
                                          onChange={(option: any) => {
                                            onChangeItem(property, option.value)
                                            setFieldValue(`${property.name}`, option ? option.value : null)
                                          }}
                                          id={property.name}
                                          onBlur={() => {
                                            setFieldTouched(property.name, true)
                                          }}
                                        />
                                      }
                                    />
                                  ),
                                  SELECT_MULTI_USER: (
                                    <FsiPropertiesInlineEditor
                                      property={property}
                                      onAccept={onAcceptItem}
                                      onRevert={onRevertItem}
                                      onCancel={onCancelItem}
                                      value={() => values?.[property.name]}
                                      isEditable={true}
                                      isRequired={false}
                                      input={
                                        <Select
                                          value={values?.[property.name]}
                                          name={property.name}
                                          placeholder={translate('ALL_USERS')}
                                          options={userOptions}
                                          multi={true}
                                          searchable
                                          disabledFilter={(user: any) => {
                                            // selection
                                            return !user.enabled
                                          }}
                                          filter={(user: any) => {
                                            // options
                                            return user.enabled || (property.value && user.id === property.value.id)
                                          }}
                                          onChange={(options: any) => {
                                            const values = options?.map((o: any) => o.value) || []
                                            onChangeItem(property, values)
                                            setFieldValue(`${property.name}`, values)
                                          }}
                                          id={property.name}
                                          onBlur={() => {
                                            setFieldTouched(property.name, true)
                                          }}
                                        />
                                      }
                                    />
                                  ),
                                  SELECT_ONE_GROUP: (
                                    <FsiPropertiesInlineEditor
                                      property={property}
                                      onAccept={onAcceptItem}
                                      onRevert={onRevertItem}
                                      onCancel={onCancelItem}
                                      value={() => values?.[property.name]}
                                      isEditable={true}
                                      isRequired={false}
                                      input={
                                        <Select
                                          value={values?.[property.name]}
                                          placeholder={translate('ALL_GROUPS')}
                                          options={groupOptionsCaRolesDisabled}
                                          multi={false}
                                          searchable
                                          name={property.name}
                                          onChange={(option: any) => {
                                            onChangeItem(property, option.value)
                                            setFieldValue(`${property.name}`, option ? option.value : null)
                                          }}
                                          id={property.name}
                                          onBlur={() => {
                                            setFieldTouched(property.name, true)
                                          }}
                                        />
                                      }
                                    />
                                  ),
                                  SELECT_MULTI_GROUP: (
                                    <FsiPropertiesInlineEditor
                                      property={property}
                                      onAccept={onAcceptItem}
                                      onRevert={onRevertItem}
                                      onCancel={onCancelItem}
                                      isEditable={true}
                                      isRequired={false}
                                      value={() => values?.[property.name]}
                                      input={
                                        <Select
                                          value={values?.[property.name]}
                                          placeholder={translate('ALL_GROUPS')}
                                          options={groupOptionsCaRolesDisabled}
                                          multi={true}
                                          searchable
                                          name={property.name}
                                          onChange={(options: any) => {
                                            const values = options?.map((o: any) => o.value) || []
                                            onChangeItem(property, values)
                                            setFieldValue(`${property.name}`, values)
                                          }}
                                          id={property.name}
                                          onBlur={() => {
                                            setFieldTouched(property.name, true)
                                          }}
                                        />
                                      }
                                    />
                                  )
                                }[property.type as MetadataType]}
                            </td>
                          </tr>
                        )
                      })}
                    </tbody>
                  </table>
                </div>
              </div>
            </Modal.Body>
            <Modal.Footer>
              <Modal.Footer.Button variant="secondary" onClick={doClose}>
                {translate('CANCEL')}
              </Modal.Footer.Button>
              <button
                className="btn btn-primary"
                type="button"
                disabled={!hasChanges}
                onClick={() => save(values)}
                style={{
                  padding: '8px 20px',
                  border: 'none',
                  borderRadius: '3px',
                  cursor: hasChanges ? 'pointer' : 'not-allowed',
                  fontSize: '14px',
                  fontWeight: 400,
                  backgroundColor: hasChanges ? '#3374C8' : '#ccc',
                  color: 'white',
                  opacity: hasChanges ? 1 : 0.6
                }}
              >
                {translate('SAVE_BTN')}
              </button>
            </Modal.Footer>
          </>
        )}
      </Formik>
    </Modal>
  )
}

export default ArtifactProperties
